import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layout } from 'antd';
import { Navigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../utils/axiosInstance';

import Sidebar from '../Layout/Sidebar';
import CustomHeader from '../Layout/Header';
import { dataUser, spocLocationDetails } from '../Helpers/util';
import { getServiceAccess } from '../../Redux/action/userActions';

import SwButton from '../Common/SwButtons/SwButtons';
import SwInput from '../Common/FormComponent/SwInput';
import SwSelect from '../Common/SwReactSelect/SwReactSelect';
import SwMultiSelect from '../Common/SwMultiSelect/SwMultiSelect';
import SwToggleButtonGroup from '../Common/SwToggleButtonGroup/SwToggleButtonGroup';
import SwKpiGroup from '../Common/SwKPI/SwKpiGroup';
import SwKpi from '../Common/SwKPI/SwKPI';
import SwModal from '../Common/SwModal/SwModal';
import SwAlert from '../Common/SwAlert/SwAlert';
import SwBadge from '../Common/SwBadge/SwBadge';
import SwLoader from '../Common/SwLoader/SwLoader';
import AntDTable from '../Common/AntDTable/AntDTable';

import MealPlanUserDrawer from './MealPlanUserDrawer';
import MealPlanAssignModal from './MealPlanAssignModal';
import MealPlanReviewTable from './MealPlanReviewTable';
import MealPlanOfferChips from './MealPlanOfferChips';

import {
  ASSIGNMENT_OPTIONS,
  buildRosterBody,
  normalizePlan,
  mealTypeTabs,
  missingMealTypes,
  unmappedOffers,
  buildMutationBody,
  groupSelectionByOffers,
  mergeMutationResults,
  defaultOfferSelection,
  actionableOffers,
  normalizeEmployeePlans,
  normalizeRows,
  locationsFromRows,
  statusMetaFor,
  isPlanExpired,
  changedCount,
  changedUserCount,
  fmtDate,
  fmtNumber,
} from './mealPlanUsersData';

/* ---------------- API ----------------
   Base path: {REACT_APP_API_GATEWAYS_URL}commerce/wallet/v2/mealPlan/{companyId}
     GET  /plans                    -> the meal offers configured for this
                                       company, each naming its meal type
     POST /roster                   -> one meal type's roster, paged and sorted
     POST /restrict                 -> remove (points kept, no further top-up)
     POST /unrestrict               -> put back (eligible from the next cycle)
     GET  /employee/{id}/plans      -> one employee across every offer

   The roster is selected by `mealType` — an array of the enum's exact strings
   (Breakfast, Lunch, Dinner) — so that is what the tabs select. The two writes
   are finer grained: they act on `walletOfferIds`, because one meal type can be
   served by several offers and the SPOC may mean only some of them. Both shapes
   live in the request builders, so no call site has to remember them.       */
const MEAL_PLAN_URL = `${process.env.REACT_APP_API_GATEWAYS_URL}commerce/wallet/v2/mealPlan`;

const MEAL_PLAN_SERVICE_ID = '28';
const PAGE_SIZE = 25;
const UNDO_WINDOW_MS = 10000;

const userDataLocal = dataUser();
const UserObj = userDataLocal ? JSON.parse(userDataLocal) : null;

/* The gateway reads the company from the path; this header mirrors the contract
   Ops published for the endpoint and is carried for parity. */
const identityHeader = () =>
  UserObj
    ? {
        user: JSON.stringify({
          sub: UserObj.email,
          email: UserObj.email,
          company_id: UserObj.company_id,
        }),
      }
    : {};

/* Every endpoint answers { statusValue, message, payload }. Unwrap it once. */
const payloadOf = response => {
  const body = response?.data;
  if (!body || body.statusValue >= 400) return null;
  return body.payload || {};
};

/* MPC-1: the section belongs to the SPOC who holds the meal plan service. The
   role claim is authoritative when present; the service-access call is the
   fallback. Either way an error denies rather than admits — a screen that fails
   open is not access control. */
const roleGrantsMealPlan = () => {
  const roles = UserObj?.role_data;
  if (!Array.isArray(roles) || !roles.length) return null; /* unknown, ask the API */
  const match = roles.find(r => String(r.service_id) === MEAL_PLAN_SERVICE_ID);
  if (!match) return false;
  return match.role_for_read === '1' || match.role_for_write === '1';
};

/* MPC-5: the trigger caption states the selection rather than a generic label. */
const locationValueRenderer = (selected, options) => {
  if (!selected.length) return 'Select Location';
  if (selected.length === options.length) return 'All locations';
  if (selected.length === 1) return selected[0].label;
  return `${selected.length} locations selected`;
};

/* The SPOC's own centres, cached at login. Ids line up with the roster's
   locationId, so the selection can be sent straight to the API. */
const spocLocations = () => {
  try {
    const parsed = JSON.parse(spocLocationDetails() || '[]');
    return Array.isArray(parsed) ? parsed.filter(l => l?.value != null) : [];
  } catch (error) {
    return [];
  }
};

function MealPlanUsers() {
  const [collapsed, setCollapsed] = useState(true);
  const companyId = UserObj?.company_id ?? null;

  /* ---------------- Access ---------------- */
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  /* ---------------- Meal types ---------------- */
  const [mealPlans, setMealPlans] = useState([]);
  const [plansLoaded, setPlansLoaded] = useState(false);
  const [mealType, setMealType] = useState(null);

  /* ---------------- Query ---------------- */
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [assignment, setAssignment] = useState('all');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  /* ---------------- Roster ---------------- */
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total: 0, assigned: 0, notAssigned: 0 });
  const [pagination, setPagination] = useState({ total: 0, from: 0, to: 0 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  /* ---------------- Interaction ---------------- */
  const [saving, setSaving] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [drawerUserId, setDrawerUserId] = useState(null);
  const [activeModal, setActiveModal] = useState(null); // assign | remove
  const [removeTarget, setRemoveTarget] = useState([]);
  const [removePreview, setRemovePreview] = useState(null);
  const [assignTarget, setAssignTarget] = useState([]);
  const [assignPreview, setAssignPreview] = useState(null);
  /* Which plans a confirmation acts on, per employee: { [userId]: offerId[] }.
     Everyone starts with every plan of the meal type; the dialog's grid is what
     varies them. One offer set per employee is the reason writes are grouped
     rather than sent as a single call. */
  const [offerSelection, setOfferSelection] = useState({});

  /* Selected people can live on pages that are no longer loaded, so a row is
     remembered when it is ticked rather than looked up in the current page. */
  const selectedRowsRef = useRef(new Map());
  const requestSeq = useRef(0);
  const undoRef = useRef(null);
  const drawerTriggerRef = useRef(null);
  const modalTriggerRef = useRef(null);

  const mealPlan = useMemo(
    () => mealPlans.find(p => p.id === mealType) || null,
    [mealPlans, mealType],
  );
  const readOnly = isPlanExpired(mealPlan);
  const tabOffers = useMemo(() => mealPlan?.plans || [], [mealPlan]);
  const disabledMealTypes = useMemo(() => missingMealTypes(mealPlans), [mealPlans]);
  const [strandedOffers, setStrandedOffers] = useState([]);

  const locationOptions = useMemo(() => {
    const cached = spocLocations();
    return cached.length ? cached : locationsFromRows(rows);
  }, [rows]);

  /* ---------------- API calls ---------------- */

  const fetchPlans = async () => {
    try {
      const response = await axiosInstance.get(`${MEAL_PLAN_URL}/${companyId}/plans`, {
        headers: identityHeader(),
      });
      const payload = payloadOf(response);
      return Array.isArray(payload?.plans) ? payload.plans : [];
    } catch (error) {
      console.error('Error fetching meal plans:', error);
      return null;
    }
  };

  const fetchRoster = async params => {
    try {
      const response = await axiosInstance.post(
        `${MEAL_PLAN_URL}/${companyId}/roster`,
        buildRosterBody(params),
        { headers: identityHeader() },
      );
      const payload = payloadOf(response);
      if (!payload) return null;
      return {
        counts: payload.counts || { total: 0, assigned: 0, notAssigned: 0 },
        pagination: payload.pagination || { page: 1, limit: 0, total: 0, from: 0, to: 0 },
        rows: Array.isArray(payload.rows) ? payload.rows : [],
      };
    } catch (error) {
      console.error('Error fetching meal plan roster:', error);
      return null;
    }
  };

  /* The platform models this as restriction, so the screen's two verbs map onto
     the two endpoints: assign -> unrestrict, unassign -> restrict. Both accept
     preview:true, which returns the same result shape and writes nothing — that
     is what powers the removal confirmation's per-employee consequences. */
  /**
   * One call per distinct plan selection.
   *
   * The endpoint takes a single offer set for a list of users, and the grid
   * lets each employee have their own — so employees who chose the same plans
   * are sent together. Unvaried selections collapse back to one call.
   *
   * The write path uses allSettled on purpose: if a later group fails, earlier
   * groups have already been applied, and reporting a flat failure would be a
   * lie. `partial` says so, and the roster is re-read either way.
   */
  const runMutation = async (action, selection, preview = false) => {
    const groups = groupSelectionByOffers(selection);
    if (!groups.length) return { summary: {}, results: [] };

    const settled = await Promise.allSettled(
      groups.map(group =>
        axiosInstance.post(
          `${MEAL_PLAN_URL}/${companyId}/${action}`,
          buildMutationBody({
            offers: mealPlan?.plans || [],
            selectedOfferIds: group.offerIds,
            userIds: group.userIds,
            preview,
          }),
          { headers: identityHeader() },
        ),
      ),
    );

    const payloads = [];
    let failed = 0;
    settled.forEach(outcome => {
      if (outcome.status !== 'fulfilled') {
        failed += 1;
        console.error(`Error running meal plan ${action}:`, outcome.reason);
        return;
      }
      const payload = payloadOf(outcome.value);
      if (payload) payloads.push(payload);
      else failed += 1;
    });

    if (!payloads.length) return null;
    return { ...mergeMutationResults(payloads), partial: failed > 0 };
  };

  /* MPC-11: one employee against every offer the company holds, in one call. */
  const fetchEmployeePlans = useCallback(
    async userId => {
      const response = await axiosInstance.get(
        `${MEAL_PLAN_URL}/${companyId}/employee/${userId}/plans`,
        { headers: identityHeader() },
      );
      const payload = payloadOf(response);
      if (!payload) throw new Error('Employee plans unavailable');
      return normalizeEmployeePlans(payload);
    },
    [companyId],
  );

  /* ---------------- Access check ---------------- */
  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      const fromRole = roleGrantsMealPlan();
      if (fromRole !== null) {
        if (!cancelled) {
          setHasAccess(fromRole);
          setAccessChecked(true);
        }
        return;
      }
      try {
        const services = await getServiceAccess(companyId);
        const match = Array.isArray(services)
          ? services.find(s => String(s.id) === MEAL_PLAN_SERVICE_ID)
          : null;
        if (!cancelled) setHasAccess(!!match?.service_access);
      } catch (error) {
        console.error('Error checking meal plan access:', error);
        if (!cancelled) setHasAccess(false);
      } finally {
        if (!cancelled) setAccessChecked(true);
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  /* ---------------- Plans ---------------- */
  useEffect(() => {
    if (!accessChecked || !hasAccess || !companyId) return undefined;
    let cancelled = false;

    (async () => {
      const raw = await fetchPlans();
      if (cancelled) return;

      if (raw === null) {
        setMealPlans([]);
        setStrandedOffers([]);
        toast.error('Could not load your meal plans. Please try again.');
      } else {
        /* Offers are grouped into the meal types the roster can address, so the
           tab the SPOC picks is exactly the value the request carries. */
        const offers = raw.map(normalizePlan);
        const tabs = mealTypeTabs(offers);
        setMealPlans(tabs);
        setStrandedOffers(unmappedOffers(offers));
        setMealType(current => current || tabs[0]?.id || null);
      }

      setPlansLoaded(true);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [accessChecked, hasAccess, companyId]);

  /* MPC-5: filter on settled input, not on every keystroke. */
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  /* MPC-2 / MPC-7: switching meal type resets row selection, the assignment
     filter and pagination — the two rosters are not comparable. */
  useEffect(() => {
    if (!mealType) return;
    selectedRowsRef.current = new Map();
    setSelectedRowKeys([]);
    setAssignment('all');
    setPage(1);
  }, [mealType]);

  useEffect(() => {
    setPage(1);
  }, [search, assignment, selectedLocations, sortBy, sortDir]);

  const locationIds = useMemo(
    () => selectedLocations.map(l => l.value),
    [selectedLocations],
  );

  const query = useMemo(
    () => ({
      mealType,
      page,
      limit: PAGE_SIZE,
      search,
      assignment,
      locationIds,
      sortBy,
      sortDir,
    }),
    [mealType, page, search, assignment, locationIds, sortBy, sortDir],
  );

  /* ---------------- Roster ---------------- */
  const loadRoster = useCallback(async () => {
    if (!mealType || !companyId) return;
    const seq = (requestSeq.current += 1);
    setLoading(true);
    setLoadError('');

    const res = await fetchRoster(query);

    /* A slower earlier request must never overwrite a newer answer. */
    if (seq !== requestSeq.current) return;

    if (res === null) {
      setRows([]);
      setCounts({ total: 0, assigned: 0, notAssigned: 0 });
      setPagination({ total: 0, from: 0, to: 0 });
      setLoadError('Could not load the employee roster.');
    } else {
      setRows(normalizeRows(res.rows));
      setCounts(res.counts);
      setPagination(res.pagination);
    }
    setLoading(false);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [companyId, mealType, query]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  /* ---------------- Selection ---------------- */
  const rememberSelection = (keys, pickedRows) => {
    const map = selectedRowsRef.current;
    pickedRows.forEach(r => r && map.set(r.id, r));
    const keySet = new Set(keys);
    [...map.keys()].forEach(k => {
      if (!keySet.has(k)) map.delete(k);
    });
    setSelectedRowKeys(keys);
  };

  const selectedRows = useMemo(
    () => selectedRowKeys.map(k => selectedRowsRef.current.get(k)).filter(Boolean),
    [selectedRowKeys],
  );

  const clearSelection = () => {
    selectedRowsRef.current = new Map();
    setSelectedRowKeys([]);
  };

  const rowFor = userId =>
    selectedRowsRef.current.get(userId) || rows.find(r => r.id === userId) || null;

  /* ---------------- Actions ---------------- */

  /* MPC-17: a bulk action is reversible for a bounded window. restrict and
     unrestrict are exact inverses, so the undo is one call rather than a
     replayed snapshot. */
  const offerUndo = (message, action, selection) => {
    undoRef.current = { action, selection };
    toast.success(
      ({ closeToast }) => (
        <div className="MealPlanUsers_UndoToast">
          <span>{message}</span>
          <button
            type="button"
            className="MealPlanUsers_LinkBtn"
            onClick={() => {
              const snap = undoRef.current;
              closeToast();
              if (snap) {
                undoRef.current = null;
                applyAction(snap.action === 'assign' ? 'unassign' : 'assign', {
                  isUndo: true,
                  selection: snap.selection,
                });
              }
            }}
          >
            Undo
          </button>
        </div>
      ),
      { autoClose: UNDO_WINDOW_MS },
    );
    setTimeout(() => {
      undoRef.current = null;
    }, UNDO_WINDOW_MS);
  };

  /**
   * @param action  'assign' | 'unassign'
   * @param userIds people to act on
   */
  const applyAction = async (action, options = {}) => {
    const { isUndo = false, selection = null } = options;
    if (!mealPlan) return;

    /* Pinned before any state reset, so the undo that follows replays exactly
       the same per-employee narrowing rather than today's grid state. */
    const targets = selection || offerSelection;
    const targetIds = Object.keys(targets).filter(id => (targets[id] || []).length);
    if (!targetIds.length) return;

    /* Who can be acted on is the endpoint's call, not ours. It answers per
       employee and plan with an outcome and, where nothing changes, a reason —
       so the whole selection is sent and the answer is reported. A client-side
       guess (for instance, refusing anyone the roster marks unregistered) would
       wrongly block a restricted member the endpoint can perfectly well
       unrestrict. */
    setSaving(true);
    const res = await runMutation(
      action === 'assign' ? 'unrestrict' : 'restrict',
      targets,
      false,
    );
    setSaving(false);

    if (!res) {
      toast.error(
        action === 'assign'
          ? 'Could not save the assignment. Please try again.'
          : 'Could not save the change. Please try again.',
      );
      return;
    }

    /* One result per employee x offer, so the people count and the change count
       are different numbers. The message is about people. */
    const changed = changedUserCount(res.results);
    const skipped = res.results.filter(r => r.outcome === 'no_change');
    const skippedPeople = new Set(skipped.map(r => r.userId)).size;

    setActiveModal(null);
    setRemoveTarget([]);
    setRemovePreview(null);
    setAssignTarget([]);
    setAssignPreview(null);
    setOfferSelection({});
    clearSelection();
    await loadRoster();

    /* Some groups landed and some did not. The roster above is the truth now;
       this only says the report below is incomplete. */
    if (res.partial) {
      toast.error(
        'Some of these changes could not be saved. The roster below shows what did apply.',
      );
    }

    if (!changed) {
      toast.info(
        action === 'assign'
          ? 'Already assigned — no change.'
          : 'No change — those employees do not hold this plan.',
      );
    } else {
      const message =
        action === 'assign'
          ? `${changed} employee${changed === 1 ? '' : 's'} assigned to the ${mealPlan.name} plan. Points are credited on the next top-up cycle.`
          : `${changed} employee${changed === 1 ? '' : 's'} removed from the ${mealPlan.name} plan. No further top-ups; issued points stay usable until expiry.`;

      if (isUndo) toast.success('Change reverted.');
      else if (targetIds.length > 1) offerUndo(message, action, targets);
      else toast.success(message);

    }

    /* MPC-15: nothing is dropped in silence — the endpoint's reason is repeated
       back, grouped so one message covers a whole batch. */
    if (skippedPeople) {
      const reasons = [...new Set(skipped.map(r => r.reason).filter(Boolean))];
      toast.info(
        `${skippedPeople} employee${skippedPeople === 1 ? '' : 's'} unchanged` +
          (reasons.length ? ` — ${reasons.join('; ')}.` : '.'),
      );
    }
  };

  /* ---------------- Modals and drawer ---------------- */
  const openModal = name => {
    modalTriggerRef.current = document.activeElement;
    setActiveModal(name);
  };

  const closeModal = () => {
    setActiveModal(null);
    setRemovePreview(null);
    setAssignPreview(null);
    const el = modalTriggerRef.current;
    /* MPC-20: focus returns to whatever opened the dialog. */
    if (el?.focus) setTimeout(() => el.focus(), 0);
  };

  /* MPC-9: the confirmation names each affected employee, their remaining points
     and what happens next. The numbers come from the endpoint's own dry run, so
     the warning cannot drift from the write that follows it.

     Opening a dialog only sets its target and resets the offer selection to the
     whole meal type. The dry run is owned by the effect below, so narrowing the
     offers re-runs it and the consequences on screen always describe the write
     that the confirm button would actually send. */
  const openRemove = userIds => {
    if (!userIds.length || !mealPlan) return;
    setRemoveTarget(userIds);
    setOfferSelection(defaultOfferSelection(userIds, rowFor, tabOffers, 'unassign'));
    openModal('remove');
  };

  const openAssign = userIds => {
    if (!userIds.length || !mealPlan) return;
    setAssignTarget(userIds);
    setOfferSelection(defaultOfferSelection(userIds, rowFor, tabOffers, 'assign'));
    openModal('assign');
  };

  const previewTarget = activeModal === 'remove' ? removeTarget : assignTarget;

  /* The grid re-runs the dry run on every tick, so it settles first: a run of
     clicks costs one round of calls, not one per checkbox. */
  useEffect(() => {
    if (!activeModal || !previewTarget.length) return undefined;

    let cancelled = false;
    const setPreview = activeModal === 'remove' ? setRemovePreview : setAssignPreview;

    /* Nobody has a plan ticked: there is nothing to ask about, and an empty
       walletOfferIds would read to the endpoint as "no narrowing" — it would
       answer for the lot. */
    if (!groupSelectionByOffers(offerSelection).length) {
      setPreview({ summary: {}, results: [] });
      return undefined;
    }

    const timer = setTimeout(async () => {
      const res = await runMutation(
        activeModal === 'remove' ? 'restrict' : 'unrestrict',
        offerSelection,
        true,
      );
      if (!cancelled) setPreview(res || { summary: {}, results: [], error: true });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [activeModal, previewTarget, offerSelection]);

  const openDrawer = (userId, event) => {
    drawerTriggerRef.current = event?.currentTarget || null;
    setDrawerUserId(userId);
  };

  const closeDrawer = () => {
    setDrawerUserId(null);
    const el = drawerTriggerRef.current;
    if (el?.focus) setTimeout(() => el.focus(), 0);
  };

  const toggleCollapsed = () => setCollapsed(prev => !prev);

  /* ---------------- Table ---------------- */
  const sortOrderFor = field =>
    sortBy === field ? (sortDir === 'asc' ? 'ascend' : 'descend') : null;

  const columns = [
    {
      title: 'Employee',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortOrderFor('name'),
      minWidth: 200,
      render: (name, record) => (
        <div className="MealPlanUsers_Employee">
          <span className="MealPlanUsers_EmployeeName">{name}</span>
          {record.dept ? <span className="SwTextGray SwTextSmall">{record.dept}</span> : null}
        </div>
      ),
    },
    {
      title: 'Email ID',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      sortOrder: sortOrderFor('email'),
      minWidth: 220,
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      sorter: true,
      sortOrder: sortOrderFor('location'),
      minWidth: 200,
    },
    {
      /* The plan's state for this employee, straight from the roster's `status`
         field. Not sortable: the endpoint sorts on name, email, location and
         points only. MPC-4 — shape and text, never colour alone. */
      title: 'Status',
      dataIndex: 'apiStatus',
      key: 'apiStatus',
      noWrap: true,
      render: value => {
        const meta = statusMetaFor(value);
        return (
          <SwBadge
            variant={meta.variant}
            icon={<i className={`bi ${meta.icon}`} />}
            text={meta.label}
          />
        );
      },
    },
    {
      /* Which plans the employee actually holds. The roster sends this as a
         comma string of the ACTIVE offers only, so a meal type served by two
         offers shows exactly the one they are on. Not sortable: the endpoint
         sorts on name, email, location and points only. */
      title: 'Meal Plans',
      dataIndex: 'walletOffers',
      key: 'walletOffers',
      minWidth: 170,
      /* Keyed by the row, so an opened cell collapses again when the page turns
         rather than carrying its expanded state onto whoever lands in that
         position next. */
      render: (names, record) => <MealPlanOfferChips key={record.id} names={names} />,
    },
    {
      /* The roster endpoint sorts on name, email, location and points only, so
         this column states its value without offering a sort it cannot honour. */
      title: 'Effective From',
      dataIndex: 'effectiveFrom',
      key: 'effectiveFrom',
      noWrap: true,
      render: value => (value ? fmtDate(value) : '–'),
    },
    {
      title: 'Points',
      dataIndex: 'points',
      key: 'points',
      sorter: true,
      sortOrder: sortOrderFor('points'),
      align: 'right',
      noWrap: true,
      className: 'MealPlanUsers_PointsCell',
      render: value => (value == null ? '–' : fmtNumber(value)),
    },
    {
      title: 'Last Updated',
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      noWrap: true,
      render: (value, record) =>
        value || record.restrictedBy
          ? `${record.restrictedBy || 'System'}${value ? `, ${fmtDate(value)}` : ''}`
          : '–',
    },
    {
      /* House pattern for a View action, as LegalAndCompliance does it: a small
         dark SwButton labelled View, with the arrow icon leading. */
      title: 'Action',
      key: 'action',
      align: 'center',
      /* An explicit width, as ExtendedV2 does for its Action column: AntDTable's
         `noWrap` only reaches the header cell, so it cannot hold a body cell
         open. What kept the button on one line, though, is .sw-btn being a flex
         row — see sw-ui.css. */
      width: 120,
      render: (_, record) => (
        <SwButton
          variant="dark"
          size="sm"
          icon={<i className="bi bi-arrow-right" />}
          iconPosition="start"
          aria-label={`View meal plans for ${record.name}`}
          onClick={event => openDrawer(record.id, event)}
        >
          View
        </SwButton>
      ),
    },
  ];

  /* MPC-7: selection survives a re-sort and a page change, and is cleared on
     plan change. */
  const rowSelection = {
    selectedRowKeys,
    onChange: rememberSelection,
    preserveSelectedRowKeys: true,
  };

  const handleTableChange = (_pagination, _filters, sorter) => {
    if (!sorter?.columnKey) return;
    if (!sorter.order) {
      setSortBy('name');
      setSortDir('asc');
      return;
    }
    setSortBy(sorter.columnKey);
    setSortDir(sorter.order === 'ascend' ? 'asc' : 'desc');
  };

  const selectedCount = selectedRowKeys.length;

  /* Each verb is offered where the selection gives it something to act on:
     Assign needs someone off at least one plan, Unassign needs someone on at
     least one. A selection that is entirely one or the other disables the
     opposite button rather than opening a dialog that can only report
     "no change".

     The test is per PLAN, not on the roster's `bucket`. `bucket` is a meal-type
     verdict — "assigned" the moment the employee holds ANY offer in it — so on
     a Lunch served by VegLunchDev, SpecialVegLunchUAT and NightNonVegLunch,
     someone on two of the three reads as assigned and the old test dead-locked
     the Assign button against ever adding the third. row.offers carries each
     plan's own status, so that is what decides.

     Registration is deliberately NOT part of this test. An unregistered employee
     still cannot hold a plan (MPC-15) — but that is explained in the assign
     dialog, which names them as blocked, rather than by a dead button that never
     says why. applyAction filters them out before the call either way. */
  const canAssignCount = selectedRows.filter(
    r => actionableOffers(r, tabOffers, 'assign').length,
  ).length;
  const canUnassignCount = selectedRows.filter(
    r => actionableOffers(r, tabOffers, 'unassign').length,
  ).length;
  const hasFilters = !!search.trim() || assignment !== 'all' || selectedLocations.length > 0;

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setAssignment('all');
    setSelectedLocations([]);
  };

  /* MPC-9: the endpoint answers per (employee x plan), so the button acts on
     people while the grid lists changes. The two are only the same number while
     a meal type has one plan. */
  const removeChanges = removePreview ? changedCount(removePreview.results) : 0;
  const removePeople = removePreview ? changedUserCount(removePreview.results) : 0;
  const hasOfferSelection = groupSelectionByOffers(offerSelection).length > 0;

  /* A plan list beside each name needs more room than the 550px confirmation. */
  const modalWidthClass = 'MealPlanUsersModal MealPlanUsersModal--grid';

  if (accessChecked && !hasAccess) return <Navigate to="/no-access" replace />;

  return (
    <Layout>
      <Sidebar collapsed={collapsed} />
      <CustomHeader collapsed={collapsed} toggleCollapsed={toggleCollapsed} />

      <Layout className={collapsed ? 'PageLayout CollapsedPageLayout' : 'PageLayout'}>
        <div className="PageContent MealPlanUsersPage">
          {/* ---------- Page heading ---------- */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="SwHeading">
                <h2>Meal Plan Users</h2>
              </div>
              <p className="SwTextGray mb-0">
                Assign and remove employees from your company&apos;s meal plans.
              </p>
            </div>
          </div>

          {!plansLoaded ? (
            <div className="SwLoaderContainer">
              <SwLoader />
            </div>
          ) : !mealPlans.length ? (
            /* MPC-1: no plans yet is a state, not an error. */
            <div className="MealPlanUsers_EmptyState">
              <i className="bi bi-basket" />
              <h3>No meal plans set up for your company</h3>
              <p className="SwTextGray">
                Meal plans are created and configured by Smartworks Operations. Raise a
                request and the team will set one up for you.
              </p>
              <Link to="/TicktingManagement" className="btn sw-btn sw-btn-primary sw-btn-sm">
                Contact Smartworks Operations
              </Link>
            </div>
          ) : (
            <>
              {/* ---------- Meal type selector (MPC-2) ----------
                  No commercial term appears here: no vendor, usable window,
                  subsidy, points per cycle or next top-up. Only where they are
                  configured, and which types are absent. */}
              <div className="MealPlanUsers_PlanCard mb-3">
                <SwToggleButtonGroup
                  type="radio"
                  name="meal-plan"
                  value={mealType}
                  onChange={value => setMealType(value)}
                  options={mealPlans.map(p => ({ label: p.name, value: p.id }))}
                  size="sm"
                />
                {/* One tab can be served by more than one wallet offer. The
                    roster cannot separate them, so the offers are named here
                    rather than pretended away — unless the tab is already the
                    offer's own name, which would just repeat itself.

                    Each name gets its own pill so two or three of them stay
                    countable, echoing the tab shape above. Deliberately lighter
                    than a tab, though: these state which plans back the meal
                    type, they are not a second row of controls. */}
                {mealPlan?.offerNames?.length &&
                mealPlan.offerNames.join(', ') !== mealPlan.name ? (
                  <div className="MealPlanUsers_ServedBy">
                    <span className="MealPlanUsers_ServedByLabel">Served by</span>
                    {mealPlan.offerNames.map(name => (
                      <span className="MealPlanUsers_ServedByTag" key={name}>
                        {name}
                      </span>
                    ))}
                  </div>
                ) : null}
                {/* MPC-2: an absent tab should read as configuration rather
                    than as a fault, so the meal types the company does not have
                    are still named. It stands on its own now that the line
                    about where Smartworks enables them has gone. */}
                {disabledMealTypes.length ? (
                  <p className="SwTextGray SwTextSmall mb-0 mt-2">
                    {disabledMealTypes.join(' and ')}{' '}
                    {disabledMealTypes.length === 1 ? 'is' : 'are'} not enabled for your
                    company, so {disabledMealTypes.length === 1 ? 'it does' : 'they do'} not
                    appear here.
                  </p>
                ) : null}
                {/* An offer whose meal type is outside the endpoint's enum has
                    no tab, because no roster can be asked for it. Naming it is
                    the only honest option — silence would read as a bug. */}
                {strandedOffers.length ? (
                  <p className="SwTextGray SwTextSmall mb-0 mt-1">
                    {strandedOffers.join(', ')}{' '}
                    {strandedOffers.length === 1 ? 'is' : 'are'} not linked to a meal
                    type, so {strandedOffers.length === 1 ? 'it has' : 'they have'} no
                    tab here. Ask Smartworks Operations to set the meal type.
                  </p>
                ) : null}
              </div>

              {/* The plan's validity is stated, not enforced: a SPOC can still
                  restrict and unrestrict at will. */}
              {readOnly ? (
                <SwAlert
                  variant="warning"
                  className="mb-3"
                  icon={<i className="bi bi-calendar-x" />}
                >
                  The {mealPlan.name} plan ran until {fmtDate(mealPlan.validTo)} and its
                  validity has lapsed. Changes here still apply, but check with Smartworks Operations
                  before relying on them.
                </SwAlert>
              ) : null}

              {/* MPC-10: this platform gives the plan to every registered employee
                  and records the exceptions, so the action here is to remove rather
                  than to add people one at a time. */}
              <SwAlert variant="info" className="mb-3" icon={<i className="bi bi-info-circle" />}>
                Every registered employee holds the {mealPlan?.name} plan automatically,
                new joiners included. Use this screen to <strong>remove</strong> the exceptions —
                removed employees keep the points they already hold.
              </SwAlert>

              {/* ---------- Summary counts (MPC-6) ---------- */}
              <SwKpiGroup className="mb-3">
                <SwKpi
                  label="Total employees"
                  value={fmtNumber(counts.total)}
                  loading={loading}
                  active={assignment === 'all'}
                  onClick={() => setAssignment('all')}
                />
                <SwKpi
                  label="Meal plan assigned"
                  value={fmtNumber(counts.assigned)}
                  loading={loading}
                  variant="success"
                  active={assignment === 'assigned'}
                  onClick={() => setAssignment(assignment === 'assigned' ? 'all' : 'assigned')}
                />
                <SwKpi
                  label="Meal plan not assigned"
                  value={fmtNumber(counts.notAssigned)}
                  loading={loading}
                  variant="grey"
                  tooltip="Includes employees who have not yet registered on Workctrl"
                  active={assignment === 'not_assigned'}
                  onClick={() =>
                    setAssignment(assignment === 'not_assigned' ? 'all' : 'not_assigned')
                  }
                />
              </SwKpiGroup>

              {/* ---------- Filters + page actions (MPC-5) ---------- */}
              <div className="row align-items-end g-2 mb-3">
                <div className="col-lg-5 col-md-6">
                  <div className="SwFormGroup mb-0">
                    <label className="SwFormLabel" htmlFor="mealPlanUserSearch">
                      Search
                    </label>
                    <div className="SwInputWithIcon">
                      <SwInput
                        id="mealPlanUserSearch"
                        placeholder="Search name, email, location or department"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                      />
                      <span className="SwInputicon">
                        <i className="bi bi-search" />
                      </span>
                    </div>
                  </div>
                </div>

                <div className="col-lg-3 col-md-3">
                  <div className="SwFormGroup mb-0">
                    <label className="SwFormLabel">Assignment</label>
                    {/* Clearing the control is how the filter is removed, so
                        'all' has no entry of its own. */}
                    <SwSelect
                      options={ASSIGNMENT_OPTIONS}
                      value={ASSIGNMENT_OPTIONS.find(o => o.value === assignment) || null}
                      onChange={option => setAssignment(option ? option.value : 'all')}
                      placeholder="All employees"
                      isClearable
                      aria-label="Filter by assignment"
                    />
                  </div>
                </div>

                <div className="col-lg-4 col-md-3">
                  <div className="SwFormGroup mb-0">
                    <label className="SwFormLabel">Location</label>
                    <SwMultiSelect
                      options={locationOptions}
                      value={selectedLocations}
                      onChange={setSelectedLocations}
                      placeholder="Select Location"
                      labelledBy="Select Location"
                      valueRenderer={locationValueRenderer}
                      hasSelectAll
                    />
                  </div>
                </div>
              </div>

              {/* ---------- Contextual action bar (MPC-7) ---------- */}
              {selectedCount > 0 ? (
                <div className="MealPlanUsers_ActionBar mb-3">
                  <span className="MealPlanUsers_ActionBarCount">{selectedCount} selected</span>
                  <div className="MealPlanUsers_ActionBarButtons">
                    {/* Brand yellow on the dark bar, never navy on navy. */}
                    <SwButton
                      variant="primary"
                      size="sm"
                      disabled={!canAssignCount}
                      title={
                        canAssignCount
                          ? undefined
                          : 'Everyone selected already holds every plan in this meal type'
                      }
                      onClick={() => openAssign(selectedRowKeys)}
                    >
                      Assign {mealPlan?.name} plan
                    </SwButton>

                    <SwButton
                      variant="danger"
                      size="sm"
                      disabled={!canUnassignCount}
                      title={
                        canUnassignCount
                          ? undefined
                          : 'Nobody selected holds a plan in this meal type'
                      }
                      onClick={() => openRemove(selectedRowKeys)}
                    >
                      Unassign {mealPlan?.name} plan
                    </SwButton>

                    <SwButton variant="light-outline" size="sm" onClick={clearSelection}>
                      Clear
                    </SwButton>
                  </div>
                </div>
              ) : null}

              {/* ---------- Roster (MPC-3) ---------- */}
              {loading && !rows.length ? (
                <div className="SwLoaderContainer">
                  <SwLoader />
                </div>
              ) : loadError ? (
                <div className="MealPlanUsers_EmptyState">
                  <i className="bi bi-exclamation-triangle" />
                  <h3>{loadError}</h3>
                  <p className="SwTextGray">
                    The roster service did not answer. Nothing has changed.
                  </p>
                  <SwButton variant="dark-outline" size="sm" onClick={loadRoster}>
                    Try again
                  </SwButton>
                </div>
              ) : rows.length ? (
                <AntDTable
                  columns={columns}
                  dataSource={rows}
                  rowKey="id"
                  loading={loading}
                  pagination
                  pageSize={PAGE_SIZE}
                  currentPage={page}
                  totalRecords={pagination.total}
                  onPageChange={setPage}
                  onChange={handleTableChange}
                  rowSelection={rowSelection}
                  scroll={{ x: 1420 }}
                />
              ) : (
                /* MPC-5: an empty roster says why, and offers one way out. */
                <div className="MealPlanUsers_EmptyState">
                  <i className="bi bi-search" />
                  <h3>No employees match these filters</h3>
                  <p className="SwTextGray">
                    {hasFilters
                      ? 'Try a different search term, or clear the filters to see the whole roster.'
                      : `Nobody appears on the ${mealPlan?.name} plan yet.`}
                  </p>
                  {hasFilters ? (
                    <SwButton variant="dark-outline" size="sm" onClick={clearFilters}>
                      Clear filters
                    </SwButton>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>
      </Layout>

      {/* ---------- Employee drawer (MPC-11) ---------- */}
      {/* MPC-11: read-only. Assign and unassign live on the roster, so there is
          one path to a write and one place its consequences are stated. */}
      <MealPlanUserDrawer
        show={!!drawerUserId}
        onClose={closeDrawer}
        employee={rowFor(drawerUserId)}
        userId={drawerUserId}
        loadPlans={fetchEmployeePlans}
      />

      {/* ---------- Assign confirmation (MPC-7) ---------- */}
      <MealPlanAssignModal
        show={activeModal === 'assign'}
        onClose={closeModal}
        plan={mealPlan}
        preview={assignPreview}
        rowFor={rowFor}
        offers={tabOffers}
        userIds={assignTarget}
        selection={offerSelection}
        onSelectionChange={setOfferSelection}
        widthClass={modalWidthClass}
        saving={saving}
        onConfirm={() => applyAction('assign')}
      />

      {/* ---------- Removal confirmation (MPC-9) ---------- */}
      <SwModal
        show={activeModal === 'remove'}
        onClose={closeModal}
        keyboard
        size="sm"
        className={modalWidthClass}
        title={`Unassign ${removeTarget.length} employee${
          removeTarget.length === 1 ? '' : 's'
        } from ${mealPlan?.name ? `the ${mealPlan.name} plan` : 'this plan'}`}
        footer={
          <>
            <SwButton
              variant="dark-outline"
              size="sm"
              onClick={closeModal}
              disabled={saving}
              autoFocus
            >
              Cancel
            </SwButton>
            <SwButton
              variant="danger"
              size="sm"
              loading={saving}
              disabled={!removeChanges || !hasOfferSelection}
              onClick={() => applyAction('unassign')}
            >
              Unassign {removePeople} employee{removePeople === 1 ? '' : 's'}
            </SwButton>
          </>
        }
      >
        {removePreview?.error ? (
          <SwAlert variant="danger" className="mb-0">
            The plan service could not confirm what this would do, so nothing has been
            changed. Close this and try again.
          </SwAlert>
        ) : (
          <>
            {/* MPC-9: which plans this acts on is part of the confirmation, not
                a hidden default, and it is set per employee — one meal type can
                carry more than one plan and the SPOC may not mean all of them
                for all of them. */}
            <MealPlanReviewTable
              action="unassign"
              offers={tabOffers}
              userIds={removeTarget}
              rowFor={rowFor}
              selection={offerSelection}
              onSelectionChange={setOfferSelection}
              results={removePreview?.results || []}
              disabled={saving}
            />

            {!hasOfferSelection ? (
              <SwAlert
                variant="warning"
                className="mb-0"
                icon={<i className="bi bi-exclamation-triangle-fill" />}
              >
                No plans are selected, so there is nothing to unassign.
              </SwAlert>
            ) : (
              <SwAlert
                variant="danger"
                className="mb-0"
                icon={<i className="bi bi-exclamation-octagon" />}
              >
                This takes effect as soon as you confirm.
              </SwAlert>
            )}
          </>
        )}
      </SwModal>
    </Layout>
  );
}

export default MealPlanUsers;
